import { apiClient } from "@/services/apiClient";
import { requestMetaPixelFlush } from "@/lib/metaPixel";
import { resolveApiBaseUrl } from "@/services/apiBaseUrl";
import type { AccountType, AuthUser } from "../types";
import {
  clearStoredAuthToken,
  resolveAuthToken,
  storeAuthToken,
} from "../utils/authToken";

export type SelectOption = {
  value: string;
  label: string;
};

type CountryResponseItem = {
  code: string;
  name: string;
};

type TimezoneResponseItem = {
  value: string;
  label: string;
};

const AUTH_SESSION_CONFIG = {
  withCredentials: true,
} as const;

const NO_REFRESH_CONFIG = {
  skipAuthRefresh: true,
} as const;

const AUTH_SESSION_NO_REFRESH_CONFIG = {
  ...AUTH_SESSION_CONFIG,
  ...NO_REFRESH_CONFIG,
} as const;

export const loginApi = async (data: {
  email: string;
  password: string;
  // The portal issuing the login fixes which account context to resolve, since
  // one email/username can back both a student and a teacher account.
  account_type?: AccountType;
  remember?: boolean;
  "g-recaptcha-response"?: string;
}) => {
  const res = await apiClient.post("/api/auth/login", data, AUTH_SESSION_NO_REFRESH_CONFIG);
  persistAuthTokenPayload(res.data, res.headers as Record<string, unknown>, data.remember);
  await apiClient.get("/api/me");

  return res.data;
};

/**
 * Where the browser is sent to begin "Continue with Google".
 *
 * A full-page navigation to the API, not an XHR: Socialite runs the OAuth
 * round trip server-side and the browser has to follow the redirects itself.
 * Everything the callback needs to finish the job travels in the query string
 * and comes back signed in the OAuth `state`.
 */
export const buildGoogleSignInUrl = (params: {
  accountType?: AccountType;
  region?: string | null;
  referralCode?: string | null;
  /** Path on this site to land on once the exchange completes. */
  returnTo?: string | null;
  locale?: string;
}) => {
  const query = new URLSearchParams();

  if (params.accountType) query.set("account_type", params.accountType);
  if (params.region) query.set("region", params.region);
  if (params.referralCode) query.set("referral_code", params.referralCode);
  if (params.returnTo) query.set("return_to", params.returnTo);
  if (params.locale) query.set("locale", params.locale);

  return `${resolveApiBaseUrl()}/api/auth/google/redirect?${query.toString()}`;
};

export type GoogleExchangeResult = {
  token: string;
  user: AuthUser;
  profile_completed: boolean;
  missing_fields: string[];
  google?: { created?: boolean };
};

/**
 * Redeems the single-use handle the Google callback put in the URL for a real
 * access token. Single-use, so this must run exactly once per landing - see
 * the callback page for how a React strict-mode double render is handled.
 */
export const exchangeGoogleTokenApi = async (token: string, remember = true) => {
  const res = await apiClient.post(
    "/api/auth/google/exchange",
    { token, remember },
    AUTH_SESSION_NO_REFRESH_CONFIG,
  );
  persistAuthTokenPayload(res.data, res.headers as Record<string, unknown>, remember);
  await apiClient.get("/api/me");

  return res.data as GoogleExchangeResult;
};

/**
 * Fills in the fields Google could not supply. Only the keys the caller was
 * actually asked for need to be present.
 */
export const completeGoogleProfileApi = async (data: {
  fname?: string;
  lname?: string;
  gender?: string;
  birthday?: string;
  country?: string;
  phone?: string;
  timezone?: string;
  terms?: boolean;
  referral_code?: string | null;
}) => {
  const res = await apiClient.put("/api/auth/google/complete-profile", data);

  return res.data as {
    user: AuthUser;
    profile_completed: boolean;
    missing_fields: string[];
  };
};

export const exchangeImpersonationTokenApi = async (token: string) => {
  const res = await apiClient.post("/api/auth/impersonate/exchange", { token }, NO_REFRESH_CONFIG);
  // Impersonation sessions shouldn't outlive the browser tab by default.
  persistAuthTokenPayload(res.data, res.headers as Record<string, unknown>, false);

  return res.data;
};

export const registerApi = async (data: {
  fname: string;
  lname: string;
  email: string;
  password: string;
  password_confirmation: string;
  region?: string | null;
  "g-recaptcha-response"?: string;
  // Optional: the profile fields moved to the second step that email and
  // Google signups share. Still accepted so a caller sending the full payload
  // keeps working.
  gender?: string;
  birthday?: string | null;
  country?: string;
  phone?: string;
  timezone?: string;
  terms?: boolean;
  referral_code?: string | null;
}) => {
  const res = await apiClient.post("/api/auth/register", data, AUTH_SESSION_NO_REFRESH_CONFIG);
  // The backend authenticates the account immediately at registration
  // (email verification, if pending, doesn't block login) and returns a
  // token alongside the `verification_sent` message.
  persistAuthTokenPayload(res.data, res.headers as Record<string, unknown>);
  await apiClient.get("/api/me");

  return res.data;
};

export const sendVerificationCodeApi = async (data: { email: string; type?: 'activation' | 'reset'; account_type?: AccountType }) => {
  const res = await apiClient.post('/api/auth/send-code', data, NO_REFRESH_CONFIG);
  return res.data;
};

export const verifyActivationApi = async (data: { email: string; code: string; account_type?: AccountType }) => {
  const payload = { email: data.email, code: data.code, account_type: data.account_type };

  const res = await apiClient.post('/api/auth/verify-activation', payload, NO_REFRESH_CONFIG);
  if (res.data && (res.data as any).token) {
    persistAuthTokenPayload(res.data, res.headers as Record<string, unknown>);
    await apiClient.get('/api/me');
    // The backend records CompleteRegistration on verification, not signup.
    // The signed-in user id doesn't change here, so the pixel won't claim on
    // its own until the next page load - ask it to claim now.
    requestMetaPixelFlush();
  }
  return res.data;
};

export const verifyResetApi = async (data: { email: string; code: string; password?: string; password_confirmation?: string; account_type?: AccountType }) => {
  // The reset email link carries a long-lived `token` (not a short code) -
  // `/api/auth/verify-reset` validates and looks records up by that field name.
  const payload: Record<string, unknown> = {
    email: data.email,
    token: data.code,
    account_type: data.account_type,
  };

  if (data.password) payload.password = data.password;
  if (data.password_confirmation) payload.password_confirmation = data.password_confirmation;

  const res = await apiClient.post('/api/auth/verify-reset', payload, NO_REFRESH_CONFIG);
  return res.data;
};

export const verifyEmailCodeApi = async (data: { email: string; code: string; type?: 'activation' | 'reset'; password?: string; account_type?: AccountType }) => {
  const payload: Record<string, unknown> = {
    email: data.email,
    code: data.code,
    account_type: data.account_type,
  };
  if (data.password) payload.password = data.password;

  const res = await apiClient.post('/api/auth/verify-code', payload, NO_REFRESH_CONFIG);
  // If token returned, persist it
  if (res.data && (res.data as any).token) {
    persistAuthTokenPayload(res.data, res.headers as Record<string, unknown>);
    await apiClient.get('/api/me');
  }
  return res.data;
};

export const forgotPasswordApi = async (data: {
  email: string;
  // The portal that requested the reset - the same email can back a student
  // and a teacher account, so this fixes which one gets the reset code.
  account_type?: AccountType;
}) => {
  const res = await apiClient.post("/api/auth/forgot-password", data, NO_REFRESH_CONFIG);

  return res.data;
};

export const resetAccountApi = async (data: {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
}) => {
  const res = await apiClient.post("/api/auth/reset-account", data, NO_REFRESH_CONFIG);

  return res.data;
};

export const logoutApi = async () => {
  const res = await apiClient.post("/api/auth/logout", undefined, AUTH_SESSION_CONFIG);
  clearStoredAuthToken();
  return res.data;
};

export const getMe = async () => {
  const res = await apiClient.get("/api/me");
  return res.data;
};

export const getRegisterMetadataApi = async (): Promise<{
  countries: SelectOption[];
  timezones: SelectOption[];
  languages: SelectOption[];
}> => {
  const [countriesResponse, timezonesResponse, languagesResponse] = await Promise.all([
    apiClient.get("/api/countries", NO_REFRESH_CONFIG),
    apiClient.get("/api/timezones", NO_REFRESH_CONFIG),
    apiClient.get("/api/languages", NO_REFRESH_CONFIG),
  ]);

  return {
    countries: normalizeCountryOptions(countriesResponse.data),
    timezones: normalizeTimezoneOptions(timezonesResponse.data),
    languages: normalizeLanguageOptions(languagesResponse.data),
  };
};

function normalizeLanguageOptions(payload: unknown): SelectOption[] {
  if (!Array.isArray(payload)) {
    throw new Error("Invalid languages response");
  }

  return payload.map((lang: any) => ({
    value: lang.code,
    label: lang.name,
  }));
}

export function persistAuthTokenPayload(payload: unknown, headers?: Record<string, unknown>, remember = true) {
  const token = resolveAuthToken(payload, headers);

  if (!token) {
    clearStoredAuthToken();
    throw new Error("Authentication token missing from response");
  }

  storeAuthToken(token, remember);
}

export const updateStudentProfileApi = async (data: {
  fname: string;
  lname: string;
  email: string;
  gender: string;
  birthday: string | null;
  country: string;
  phone: string;
  timezone: string;
  edit_password: boolean;
  password?: string;
  password_confirmation?: string;
  image?: File | null;
  remove_image?: boolean;
  lecture_reminder_email?: boolean;
  lecture_reminder_whatsapp?: boolean;
  auto_record_lectures?: boolean;
  // "" (or omitted) means "use regional default" - the backend normalizes an
  // empty value back to null, distinct from submitting 'en'/'ar' explicitly.
  whatsapp_content_language?: string;
}) => {
  const formData = new FormData();
  
  formData.append("fname", data.fname);
  formData.append("lname", data.lname);
  formData.append("email", data.email);
  formData.append("gender", data.gender);
  if (data.birthday) formData.append("birthday", data.birthday);
  formData.append("country", data.country);
  formData.append("phone", data.phone);
  formData.append("timezone", data.timezone);
  formData.append("edit_password", data.edit_password ? "1" : "0");
  
  if (data.lecture_reminder_email !== undefined) {
    formData.append("lecture_reminder_email", data.lecture_reminder_email ? "1" : "0");
  }
  if (data.lecture_reminder_whatsapp !== undefined) {
    formData.append("lecture_reminder_whatsapp", data.lecture_reminder_whatsapp ? "1" : "0");
  }
  if (data.auto_record_lectures !== undefined) {
    formData.append("auto_record_lectures", data.auto_record_lectures ? "1" : "0");
  }
  // Always appended, even empty - an empty string is how "use regional
  // default" (null) travels over FormData, and the backend must be able to
  // tell "submitted empty" apart from "field omitted".
  formData.append("whatsapp_content_language", data.whatsapp_content_language ?? "");

  if (data.edit_password && data.password) {
    formData.append("password", data.password);
    formData.append("password_confirmation", data.password_confirmation || "");
  }

  if (data.image) {
    formData.append("image", data.image);
  }

  if (data.remove_image) {
    formData.append("remove_image", "1");
  }

  // Use POST with _method=PUT to ensure multipart/form-data works correctly with files
  const res = await apiClient.post("/api/me/profile", formData, {
    params: { _method: "PUT" },
  });

  return res.data as {
    message: string;
    user: AuthUser;
  };
};

export const updateTeacherProfileApi = async (data: {
  birthday: string | null;
  country: string;
  phone: string;
  timezone: string;
  whatsapp_content_language: string;
  edit_password: boolean;
  password?: string;
  password_confirmation?: string;
}) => {
  // Identity fields (name, Arabic name, email, gender, avatar) are locked for
  // teachers and intentionally not sent; the backend ignores them as well.
  const formData = new FormData();

  if (data.birthday) formData.append("birthday", data.birthday);
  formData.append("country", data.country);
  formData.append("phone", data.phone);
  formData.append("timezone", data.timezone);
  formData.append("whatsapp_content_language", data.whatsapp_content_language);
  formData.append("edit_password", data.edit_password ? "1" : "0");

  if (data.edit_password && data.password) {
    formData.append("password", data.password);
    formData.append("password_confirmation", data.password_confirmation || "");
  }

  // Use POST with _method=PUT for multipart/form-data compatibility
  const res = await apiClient.post("/api/me/teacher-profile", formData, {
    params: { _method: "PUT" },
  });

  return res.data as {
    message: string;
    user: AuthUser;
  };
};

function normalizeCountryOptions(payload: unknown): SelectOption[] {
  if (!Array.isArray(payload)) {
    throw new Error("Invalid countries response");
  }

  return payload
    .filter(isCountryResponseItem)
    .map((country) => ({
      value: country.code,
      label: country.name,
    }));
}

function normalizeTimezoneOptions(payload: unknown): SelectOption[] {
  if (!Array.isArray(payload)) {
    throw new Error("Invalid timezones response");
  }

  return payload
    .filter(isTimezoneResponseItem)
    .map((timezone) => ({
      value: timezone.value,
      label: timezone.label,
    }));
}

function isCountryResponseItem(value: unknown): value is CountryResponseItem {
  return isRecord(value) && isNonEmptyString(value.code) && isNonEmptyString(value.name);
}

function isTimezoneResponseItem(value: unknown): value is TimezoneResponseItem {
  return isRecord(value) && isNonEmptyString(value.value) && isNonEmptyString(value.label);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}
